import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  useMaturityFrameworks,
  useMaturityComparison,
  useMaturityAssessments,
  type MaturityComparisonVariables,
} from '../../../graphql/queries';
import { MaturityAssessmentStatus } from '../../../graphql/types';
import { MaturityComparisonChart } from '../../../components/maturity';
import { EmptyState, TableSkeleton } from '../../../components/shared';
import '@tylertech/forge/card';
import '@tylertech/forge/select';
import './index.css';

export const Route = createFileRoute('/maturity/comparison/')({
  component: MaturityComparisonPage,
});

export { MaturityComparisonPage };

function MaturityComparisonPage(): JSX.Element {
  const CardElement = 'forge-card' as React.ElementType;

  const [selectedFrameworkId, setSelectedFrameworkId] = useState<string>('');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');

  const { data: frameworksData, loading: frameworksLoading } = useMaturityFrameworks(50);
  const frameworks = frameworksData?.maturityFrameworks?.edges?.map((e) => e.node) ?? [];

  // Fetch all approved assessments for the selected framework to drive comparison
  const { data: assessmentsData, loading: assessmentsLoading } = useMaturityAssessments(
    selectedFrameworkId
      ? { frameworkId: selectedFrameworkId, status: MaturityAssessmentStatus.Approved }
      : undefined,
    100
  );
  const allAssessments = assessmentsData?.maturityAssessments?.edges?.map((e) => e.node) ?? [];

  // Derive available periods
  const periods = Array.from(new Set(allAssessments.map((a) => a.assessmentPeriod))).sort();

  // Derive subject IDs from filtered assessments for the chosen period
  const periodAssessments = selectedPeriod
    ? allAssessments.filter((a) => a.assessmentPeriod === selectedPeriod)
    : allAssessments;

  const subjectIds = Array.from(new Set(periodAssessments.map((a) => a.subjectEntityId)));

  const compVars: MaturityComparisonVariables = {
    frameworkId: selectedFrameworkId,
    subjectIds,
    assessmentPeriod: selectedPeriod || undefined,
  };

  const { data: compData, loading: compLoading } = useMaturityComparison(
    compVars,
    !selectedFrameworkId || subjectIds.length < 2
  );

  const comparisonSubjects = compData?.maturityComparison ?? [];

  // Get categories from the selected framework
  const selectedFramework = frameworks.find((fw) => fw.entityId === selectedFrameworkId);
  const categories = selectedFramework
    ? selectedFramework.areas.flatMap((a) => a.categories.map((c) => ({ id: c.id, name: c.name })))
    : [];

  const isLoading = frameworksLoading || assessmentsLoading || compLoading;

  return (
    <div className="maturity-comparison-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="forge-typography--headline5">Maturity Comparison</h1>
          <p className="forge-typography--body2">
            Compare maturity scores across services and teams for a given framework and period.
          </p>
        </div>
      </div>

      {/* Controls */}
      <CardElement>
        <div className="maturity-comparison-page__section">
          <h2 className="forge-typography--subtitle1 maturity-comparison-page__section-title">
            Filters
          </h2>
          <div className="maturity-comparison-page__controls">
            <div>
              <label className="forge-typography--caption" htmlFor="framework-select">
                Framework
              </label>
              <select
                id="framework-select"
                value={selectedFrameworkId}
                onChange={(e) => {
                  setSelectedFrameworkId(e.target.value);
                  setSelectedPeriod('');
                }}
                aria-label="Select framework"
              >
                <option value="">— Select a framework —</option>
                {frameworks.map((fw) => (
                  <option key={fw.entityId} value={fw.entityId}>
                    {fw.name} (v{fw.version})
                  </option>
                ))}
              </select>
            </div>

            {periods.length > 0 && (
              <div>
                <label className="forge-typography--caption" htmlFor="period-select">
                  Assessment Period
                </label>
                <select
                  id="period-select"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  aria-label="Select assessment period"
                >
                  <option value="">All periods</option>
                  {periods.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </CardElement>

      {/* Comparison chart */}
      <CardElement>
        <div className="maturity-comparison-page__section">
          <h2 className="forge-typography--subtitle1 maturity-comparison-page__section-title">
            Radar Comparison
          </h2>

          {isLoading ? (
            <TableSkeleton />
          ) : !selectedFrameworkId ? (
            <EmptyState
              icon="bar_chart"
              title="Select a framework"
              description="Choose a framework above to see a comparison across subjects."
            />
          ) : subjectIds.length < 2 ? (
            <EmptyState
              icon="group"
              title="Not enough subjects"
              description="At least 2 subjects with approved assessments are required to show a comparison."
            />
          ) : comparisonSubjects.length < 2 ? (
            <EmptyState
              icon="group"
              title="Comparison data loading"
              description="Comparison data will appear once subjects are resolved."
            />
          ) : (
            <MaturityComparisonChart
              subjects={comparisonSubjects}
              categories={categories}
              size={440}
            />
          )}
        </div>
      </CardElement>
    </div>
  );
}
